import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { Passport, DigitalPassport, Skillstree } from '@/types/achievement';
import { useAchievements } from '../../hooks/useAchievements';
import { achievementsMapper } from '../../utils/achievementsMapper';

interface DigitalPassportsOverviewProps {
  filteredPassports?: Passport[]|DigitalPassport[];
  setActiveTab: (tab: string) => void;
  onPassportClick?: (passport: Passport) => void;
  showAll?: boolean;
}

const DigitalPassportsOverview: React.FC<DigitalPassportsOverviewProps> = ({ 
  filteredPassports,
  setActiveTab, 
  onPassportClick,
  showAll = false 
}) => {
  let displayPassports: any;
  if (filteredPassports && filteredPassports?.length) {
    displayPassports = showAll ? filteredPassports : (filteredPassports?.length ? filteredPassports.slice(0, 2) : filteredPassports);
  } else {
    const { passports } = useAchievements();
    displayPassports = showAll ? passports : (passports?.length ? passports.filter(p => p.completed_at !== null).slice(0, 2) : passports);
  }
  // console.log('displayPassports:', displayPassports);

  return (
    <section className="overview-tab passport-section">
      {!showAll && (
        <div className="flex flex-row justify-between items-stretch pt-4">
          <div className="flex flex-row items-start">
            <div className="w-9 h-9 mt-2 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl shadow-lg flex items-center justify-center">
              <img className="h-7 w-auto object-center" src="/pages/achievements/Digital-Passports.png" alt="Digital Passports"/>
            </div>
            <div className="flex flex-col items-start ms-3">
              <span className="text-xl text-white">Digital Passport</span>
              <span className="text-sm text-white/60">Digital credentials for job roles</span>
            </div>
          </div>
          <div className="items-end">
            <button 
              className="bg-transparent text-white border-0 focus:border-0 hover:border-0 no-underline font-normal text-sm"
              onClick={() => setActiveTab('passport')}
            >
              View All <ChevronRight size={16} className="inline -mt-1 ml-1"/>
            </button>
          </div>
        </div>
      )}

      <div className={`grid ${showAll ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-8 items-stretch py-8`}>
        {displayPassports.map((passport, index) => (
          <DigitalPassportCard 
            key={index} 
            passport={passport as Passport} 
            onClick={onPassportClick}
            compact={!showAll}
          />
        ))}
      </div>
    </section>
  );
};

const DigitalPassportCard: React.FC<{ 
  passport: Passport;
  onClick?: (passport: Passport) => void;
  compact?: boolean;
}> = ({ passport, onClick, compact = false }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(passport);
    }
  };

  // console.log(passport);

  const role = passport.role || (passport.skillstree as Skillstree)?.name || null;
  const image = passport.image || (passport.skillstree as Skillstree)?.image_url || "/pages/achievements/skilltrue-passport.png";
  const field = passport.field || passport.category || (passport as Passport).category_name || null;
  const badgesEarned = passport.badgesTotal || passport.badgesCount || passport.badges?.length || 0;
  const microskillsCount = passport.microskillsCount || badgesEarned || 0;
  const microskillsTotal = passport.microskillsTotal || badgesEarned || 0;
  const microskillsDisplay =  microskillsCount != microskillsTotal ? `${microskillsCount}/${microskillsTotal}` : microskillsTotal;
  const verifiedBy = passport.verifiedBy || "PEOPLElogy Digital Sdn Bhd";
  const passportId = passport.passportId || passport.unique_code || passport.id;

  return (
    <div 
      className={`flex flex-col space-y-4 bg-[#174EA0] border-2 border-blue-600 rounded-lg shadow-sm px-6 py-6 cursor-pointer transition-colors ${
        compact ? 'min-h-[300px]' : ''
      }`}
      onClick={handleClick}
    >
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="flex flex-col items-center md:flex-row md:items-start">
          <img className="flex h-auto max-w-16 min-w-16 object-center" src={image} alt={role || undefined} onError={(e) => e.currentTarget.src = '/pages/achievements/skilltrue-passport.png'}/>
          <div className="flex flex-col items-center md:items-start md:ps-3">
            <span className="text-lg font-normal text-white mb-1 mt-2 md:mt-0 text-center md:text-left">{role}</span>
            {field && <span className="text-sm font-medium bg-blue-100 text-blue-900 rounded-sm py-0.5 px-2.5">{field}</span>}
          </div>
        </div>
        <div className="flex flex-row items-center justify-between gap-2 w-full md:w-auto md:flex-col md:items-end">
          <span className="text-xs font-normal text-white/60 md:order-2 whitespace-nowrap min-w-fit flex-shrink-0">Passport ID</span>
          <span className="text-sm font-normal text-blue-300 md:order-1 md:mb-0.5 flex-1 min-w-0 break-words text-right md:text-right">{passportId}</span>
        </div>
      </div>
      
      {/* {!compact && ( */}
        {/* <> */}
          <div className="flex flex-col items-start py-5 px-4 bg-black/30 rounded-lg">
            {role && <span className="text-lg font-normal text-white mb-1">Skillstree: {role}</span>}
            <span className="text-base font-normal text-white/60">Verified by {verifiedBy}</span>
          </div>
          <div className="grid grid-cols-2 gap-5 justify-between items-stretch">
            <div className="flex flex-col items-center py-5 px-4 bg-black/30 rounded-lg">
              <span className="text-2xl font-normal text-white mb-1">{badgesEarned}</span>
              <span className="text-base font-light text-white/60">Badges Earned</span>    
            </div>
            <div className="flex flex-col items-center py-5 px-4 bg-black/30 rounded-lg">
              <span className="text-2xl font-normal text-white mb-1">{microskillsDisplay}</span>
              <span className="text-base font-light text-white/60">Microskills</span>    
            </div>
          </div>
          <div className="flex flex-col items-center text-base font-normal text-white/70">
            Completed on {achievementsMapper.formatDate(passport.completed_at as string)}
          </div>
          <div className="flex flex-col items-center">
            <button className="w-1/3 text-center bg-white hover:bg-black/30 border border-blue-950/20 text-blue-800 hover:text-white px-6 py-1.5 rounded-full transition-colors">
              View Details
            </button>
          </div>
        {/* </> */}
      {/*  )} */}
    </div>
  );
};

export default DigitalPassportsOverview;